// src/shared/chartUtils.ts
import * as am5 from "@amcharts/amcharts5";
import * as am5plugins_exporting from "@amcharts/amcharts5/plugins/exporting";
import { t, TFunction } from "i18next";

export function numberFormat(
  number: number,
  decimals = 2,
  decPoint = ".",
  thousandsSep = ","
): string {
  if (isNaN(number)) return "0";
  const fixedNumber = number.toFixed(decimals);
  const parts = fixedNumber.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
  return parts.join(decPoint);
}

export function configureExporting(chart: am5.Root, data: any) {
  const exporting = am5plugins_exporting.Exporting.new(chart, {
    menu: am5plugins_exporting.ExportingMenu.new(chart, {
      items: [
        {
          type: "format",
          format: "csv",
          label: "Download CSV",
        },
        {
          type: "format",
          format: "png",
          label: "Download PNG",
        },
        {
          type: "format",
          format: "jpg",
          label: "Download JPG",
        },
      ],
    }),
    dataSource: data,
  });
}

declare global {
  interface Window {
    am5RootInstances?: { [key: string]: am5.Root };
  }
}

export function initializeAmChart(chartId: any) {
  if (!window.am5RootInstances) {
    window.am5RootInstances = {};
  }

  if (window.am5RootInstances[chartId]) {
    window.am5RootInstances[chartId].dispose();
    delete window.am5RootInstances[chartId];
  }

  const root = am5.Root.new(chartId);
  window.am5RootInstances[chartId] = root;

  return root;
}

export const generateYearsOptions = (startYear: number, endYear: number) => {
  const years = [];
  for (let year = startYear; year <= endYear; year++) {
    years.push({ id: year, name: year.toString() });
  }
  return years;
};
export type Language = "es" | "pt" | "en";

export const months: Record<Language, string[]> = {
  es: [
    "Ene",
    "Feb",
    "Mar",
    "Abr",
    "May",
    "Jun",
    "Jul",
    "Ago",
    "Sep",
    "Oct",
    "Nov",
    "Dic",
  ],
  en: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ],
  pt: [
    "Jan",
    "Fev",
    "Mar",
    "Abr",
    "Mai",
    "Jun",
    "Jul",
    "Ago",
    "Set",
    "Out",
    "Nov",
    "Dez",
  ],
};

export const getMonthsOptions = (t: TFunction) => [
  { id: 1, name: t("share.views.january") },
  { id: 2, name: t("share.views.february") },
  { id: 3, name: t("share.views.march") },
  { id: 4, name: t("share.views.april") },
  { id: 5, name: t("share.views.may") },
  { id: 6, name: t("share.views.june") },
  { id: 7, name: t("share.views.july") },
  { id: 8, name: t("share.views.august") },
  { id: 9, name: t("share.views.september") },
  { id: 10, name: t("share.views.october") },
  { id: 11, name: t("share.views.november") },
  { id: 12, name: t("share.views.december") },
];

export const monthsOptions = [
  { id: 1, name: t("share.views.january") },
  { id: 2, name: t("share.views.february") },
  { id: 3, name: t("share.views.march") },
  { id: 4, name: t("share.views.april") },
  { id: 5, name: t("share.views.may") },
  { id: 6, name: t("share.views.june") },
  { id: 7, name: t("share.views.july") },
  { id: 8, name: t("share.views.august") },
  { id: 9, name: t("share.views.september") },
  { id: 10, name: t("share.views.october") },
  { id: 11, name: t("share.views.november") },
  { id: 12, name: t("share.views.december") },
];

export const colorsKpi = [
  "#68478D",
  "#FF0070",
  "#F2AF41",
  "#2196f3",
  "#ff5722",
  "#e91e63",
  "#795548",
  "#3f51b5",
  "#2196f3",
  "#10e5a5",
  "#ff5100",
  "#68478D",
  "#0d97e8",
  "#9b0fe1",
  "#f2af41",
  "#d3d3d3",
  "#68478D",
  "#FF0070",
  "#F2AF41",
  "#2196f3",
  "#ff5722",
  "#e91e63",
  "#795548",
  "#3f51b5",
  "#2196f3",
  "#10e5a5",
  "#ff5100",
  "#68478D",
  "#0d97e8",
  "#9b0fe1",
  "#f2af41",
  "#d3d3d3",
  "#55f3c4",
  "#fa006e",
  "#a083c0",
  "#7852a3",
  "#ff358e",
  "#f2b147",
  "#b1b1b1",
  "#a67a6a",
  "#e39310",
  "#c25cf4",
  "#a286c2",
  "#b199cc",
  "#c1a196",
  "#3ef2bc",
  "#339ff4",
  "#37f1b9",
  "#0ecc93",
  "#fd5000",
  "#0d97e8",
  "#a37564",
  "#6c4a93",
  "#ee568a",
  "#818181",
  "#be9e93",
  "#c057f4",
  "#66f4ca",
  "#f06192",
  "#ff0372",
  "#ef9c14",
  "#40a5f5",
  "#3aa2f4",
  "#7a53a5",
  "#ea9710",
  "#5062c2",
  "#979797",
  "#ff0b76",
  "#9878bb",
  "#ff5100",
  "#ff8a66",
  "#a184c1",
  "#ababab",
  "#7b7b7b",
  "#3949a3",
  "#b0887a",
  "#da0060",
  "#9f9f9f",
  "#a97e6e",
  "#41a5f5",
  "#5162c2",
  "#9879bc",
  "#f1a72e",
  "#a87c6d",
  "#959595",
  "#c564f5",
  "#8f6cb5",
  "#ed0068",
  "#db1559",
  "#8c0ecb",
  "#0b77cd",
  "#e20063",
  "#47f2bf",
  "#7953a5",
  "#aaaaaa",
  "#999999",
  "#6e6e6e",
  "#2497f3",
  "#9270b7",
  "#ff5823",
  "#4c5ec1",
  "#d3890e",
  "#bd51f3",
  "#ad94c9",
  "#9e9e9e",
  "#ff8964",
  "#9a7bbd",
  "#68478d",
  "#0c86cd",
  "#8560af",
  "#2c9bf4",
  "#8c69b3",
  "#6df5cc",
  "#ff5722",
  "#dd165a",
  "#b335f2",
  "#afafaf",
  "#4053b9",
  "#9f6f5e",
  "#f4bb5e",
  "#949494",
  "#ce850e",
  "#e79510",
  "#ba47f3",
  "#bf9e93",
  "#0c86e7",
  "#b18a7c",
  "#ff4e16",
  "#68c2f7",
  "#10e5a5",
  "#747474",
  "#8b97d7",
  "#8b8b8b",
  "#6b6b6b",
  "#f4ba5b",
  "#845eae",
  "#0b83c9",
  "#a9a9a9",
  "#bc4cf3",
  "#ee9a10",
  "#ef9d17",
  "#3f51b5",
  "#d71557",
  "#d04200",
  "#58b0f6",
  "#8f6db5",
  "#ff509d",
  "#0d89ec",
  "#ff793b",
  "#815bac",
  "#d3d3d3",
  "#6d4b94",
  "#8c8c8c",
  "#845fae",
  "#dd0061",
  "#c2a398",
  "#118ef2",
  "#35f1b9",
  "#ae28f1",
  "#0b79d0",
  "#4052b7",
  "#25a7f3",
  "#737373",
  "#ff7a3d",
  "#7c7c7c",
  "#0c83e2",
  "#7d7d7d",
  "#c61350",
  "#7f57ab",
  "#2498f3",
  "#7a7a7a",
  "#c1a397",
  "#727272",
  "#795548",
  "#da4500",
  "#354499",
  "#d83400",
  "#36aef4",
  "#898989",
  "#ea2d6d",
  "#a3a3a3",
  "#ff6b3d",
  "#815a4c",
  "#0e9df2",
  "#0b72c4",
  "#56aff6",
  "#14a0f2",
  "#3fb1f4",
  "#6cbaf7",
  "#ff4206",
  "#855e4f",
  "#ff61a7",
  "#acacac",
  "#9b0fe1",
  "#e91e63",
  "#ff7433",
  "#a410ef",
  "#ff844a",
  "#5fbef6",
  "#64f4c9",
  "#ff5ea4",
  "#f53b00",
  "#ec3b77",
  "#ac24f0",
  "#c07c0d",
  "#6a6a6a",
  "#d2880e",
  "#f2b043",
  "#61bff6",
  "#9d9d9d",
  "#9c6d5d",
  "#5c6cc6",
  "#ff4c9a",
  "#8661af",
  "#0c90de",
  "#ff7434",
  "#5566c4",
  "#35adf4",
  "#2196f3",
  "#7b54a7",
  "#0c7fdb",
  "#9f82c0",
  "#f2af41",
  "#767676",
  "#f4ba5c",
  "#c5a89e",
  "#9b9b9b",
  "#e94a00",
  "#c88b5e",
  "#bcbabc",
  "#60af92",
  "#2e97c7",
  "#f3d483",
  "#acacac",
  "#d47b0d",
  "#7a52a3",
  "#9c005a",
  "#ff6177",
  "#bd72cd",
  "#ffac41",
  "#82717b",
  "#0e87b6",
  "#a1836a",
  "#e2700e",
  "#ff4d62",
  "#a98e5e",
  "#7985d0",
  "#c472f3",
  "#ef8e2f",
  "#9a79bd",
  "#816453",
  "#ffb062",
  "#cb844d",
  "#ff625f",
  "#d1a57a",
  "#b29d6d",
  "#d0796b",
  "#c3ac67",
  "#be9087",
  "#8f5a83",
  "#ff742e",
  "#f1b17d",
  "#c89450",
];
